import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { fetchSalesmanConsolidatedReport } from "../../redux/actions/salesmanAction";
import { FullScreenLoader } from "../Components/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import {
  FiUsers,
  FiCheckCircle,
  FiGift,
  FiTrendingUp,
  FiCalendar,
  FiRefreshCw,
} from "react-icons/fi";

const SalesmanOverview = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const dashboardData = useSelector((state) => state.dashboard?.data);
  const {
    consolidatedReport,
    reportLoading,
    reportError,
  } = useSelector((state) => state.salesman || {});

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);

  // Get salesman user_id from dashboard data or userInfo
  const salesmanId = dashboardData?.user_id || userInfo?.user_id;

  const fetchReport = React.useCallback(async () => {
    if (!salesmanId || !userInfo?.access_token) return;

    const requestData = {
      salesman_id: salesmanId,
    };

    if (startDate) {
      requestData.start_date = dayjs(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      requestData.end_date = dayjs(endDate).format("YYYY-MM-DD");
    }

    await dispatch(fetchSalesmanConsolidatedReport(requestData, userInfo.access_token));
  }, [salesmanId, userInfo?.access_token, startDate, endDate, dispatch]);

  useEffect(() => {
    if (salesmanId && userInfo?.access_token) {
      fetchReport();
    }
  }, [salesmanId, userInfo?.access_token, fetchReport]);

  const handleDateRangeChange = (update) => {
    setDateRange(update);
    if (update[0]) {
      setStartDate(update[0]);
    } else {
      setStartDate(null);
    }
    if (update[1]) {
      setEndDate(update[1]);
    } else {
      setEndDate(null);
    }
  };

  const handleApplyFilter = () => {
    fetchReport();
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setDateRange([null, null]);
    // Fetch report without date filters
    setTimeout(() => {
      fetchReport();
    }, 100);
  };

  const summary = consolidatedReport?.summary || {};
  const details = consolidatedReport?.details || {};
  const salesmanInfo = consolidatedReport?.salesman || {};

  return (
    <DashboardLayout>
      <FullScreenLoader 
        show={reportLoading} 
        message="Loading overview data..." 
        variant="primary"
        blurIntensity="md"
      />

      <div className="max-w-full mx-auto px-2 sm:px-4 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            Salesman Overview
          </h1>
          {salesmanInfo?.name && (
            <p className="text-gray-600 text-xs sm:text-sm md:text-base">
              Performance report for <span className="font-semibold">{salesmanInfo.name}</span>
            </p>
          )}
        </div>

        {/* Date Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6 border border-amber-200">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <FiCalendar className="text-amber-600 flex-shrink-0" size={18} />
              <span className="font-medium text-gray-700 text-sm sm:text-base">Filter by Date Range:</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <DatePicker
                selectsRange
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={handleDateRangeChange}
                isClearable
                placeholderText="Select date range"
                dateFormat="dd MMM yyyy"
                className="w-full sm:flex-1 px-3 sm:px-4 py-2 border-2 border-amber-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleApplyFilter}
                  disabled={reportLoading}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium text-xs sm:text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <FiRefreshCw size={14} className="sm:w-4 sm:h-4" />
                  <span>Apply</span>
                </button>
                {(startDate || endDate) && (
                  <button
                    onClick={handleClearFilter}
                    disabled={reportLoading}
                    className="px-3 sm:px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg font-medium text-xs sm:text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
          {(consolidatedReport?.filters?.start_date || consolidatedReport?.filters?.end_date) && (
            <div className="mt-3 text-xs sm:text-sm text-gray-600">
              <span className="font-medium">Active Filters:</span>{" "}
              {consolidatedReport.filters.start_date && (
                <span>From: {dayjs(consolidatedReport.filters.start_date).format("DD MMM YYYY")}</span>
              )}
              {consolidatedReport.filters.start_date && consolidatedReport.filters.end_date && " | "}
              {consolidatedReport.filters.end_date && (
                <span>To: {dayjs(consolidatedReport.filters.end_date).format("DD MMM YYYY")}</span>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {reportError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">
              <strong>Error:</strong> {reportError?.message || "Failed to load overview data"}
            </p>
          </div>
        )}

        {/* Summary Cards */}
        {consolidatedReport && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">
                      QR Codes Accumulated
                    </p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">
                      {summary.total_qr_codes_accumulated || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                    <FiCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">
                      QR Codes Redeemed
                    </p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600">
                      {summary.total_qr_codes_redeemed || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                    <FiTrendingUp className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">
                      Gifts Redeemed
                    </p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600">
                      {summary.total_gifts_redeemed || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                    <FiGift className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">
                      Customers Created
                    </p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">
                      {summary.total_customers_created || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-full p-2 sm:p-3 md:p-4 flex-shrink-0">
                    <FiUsers className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Details Sections */}
            {/* <div className="space-y-4 sm:space-y-6">
             
              {details.accumulated_qr_codes && details.accumulated_qr_codes.length > 0 && (
                <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-green-200 p-3 sm:p-4 md:p-6">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                    <FiCheckCircle className="text-green-600 flex-shrink-0" size={20} />
                    <span>Accumulated QR Codes ({details.accumulated_qr_codes.length})</span>
                  </h2>
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                      <table className="min-w-full bg-white text-xs sm:text-sm">
                        <thead className="bg-green-500 text-white">
                          <tr>
                            <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs sm:text-sm">QR ID</th>
                            <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs sm:text-sm">Customer</th>
                            <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs sm:text-sm">Mobile</th>
                            <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-xs sm:text-sm">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {details.accumulated_qr_codes.map((qr) => (
                            <tr key={qr.id} className="hover:bg-green-50">
                              <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm break-all">{qr.qr_id || "-"}</td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">{qr.customer_name || "-"}</td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">{qr.customer_mobile || "-"}</td>
                              <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">
                                {qr.created_at
                                  ? dayjs(qr.created_at).format("DD MMM YYYY")
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

           

              
              {!reportLoading &&
                !reportError &&
                consolidatedReport &&
                (!details.accumulated_qr_codes || details.accumulated_qr_codes.length === 0) &&
                (!details.redeemed_qr_codes || details.redeemed_qr_codes.length === 0) &&
                (!details.gifts_redeemed || details.gifts_redeemed.length === 0) && (
                  <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border-2 border-gray-200 p-6 sm:p-8 md:p-12 text-center">
                    <FiTrendingUp className="mx-auto text-gray-400 text-4xl sm:text-5xl mb-4" />
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">No Data Available</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      {startDate || endDate
                        ? "No data found for the selected date range. Try adjusting your filters."
                        : "No activity data available yet. Start by scanning QR codes and redeeming gifts."}
                    </p>
                  </div>
                )}
            </div> */}

            {/* Note */}
            {/* {consolidatedReport?.note && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> {consolidatedReport.note}
                </p>
              </div>
            )} */}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SalesmanOverview;

